import {
  memo,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type UIEvent,
} from "react";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { getChartSeriesColor } from "@/constants/chartColors";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { GSC_MAX_COMPARE_DOMAINS } from "@/types/gsc";

export interface CompareDomainSelectorProps {
  selectedDomains?: readonly string[];
  availableDomains?: readonly string[];
  onChange: (domains: string[]) => void;
  maxDomains?: number;
  isLoading?: boolean;
  className?: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const DOMAIN_PAGE_SIZE = 50;

const sortDomains = (domains: readonly string[]): string[] =>
  [...domains].sort((a, b) => a.localeCompare(b));

const domainDotColor = (
  domain: string,
  availableDomains: readonly string[],
): string => {
  const index = availableDomains.indexOf(domain);
  return getChartSeriesColor(index >= 0 ? index : 0);
};

function CompareDomainSelectorComponent({
  selectedDomains = [],
  availableDomains = [],
  onChange,
  maxDomains = GSC_MAX_COMPARE_DOMAINS,
  isLoading = false,
  className,
}: CompareDomainSelectorProps) {
  const safeSelectedDomains = Array.isArray(selectedDomains)
    ? selectedDomains
    : [];
  const safeAvailableDomains = Array.isArray(availableDomains)
    ? availableDomains
    : [];

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(DOMAIN_PAGE_SIZE);
  const debouncedQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);

  const selectedSet = useMemo(
    (): ReadonlySet<string> => new Set(safeSelectedDomains),
    [safeSelectedDomains],
  );

  const sortedOptions = useMemo(
    (): string[] => sortDomains(safeAvailableDomains),
    [safeAvailableDomains],
  );

  const addableDomains = useMemo(
    (): string[] =>
      sortedOptions.filter((domain) => !selectedSet.has(domain)),
    [sortedOptions, selectedSet],
  );

  const filteredAddableDomains = useMemo((): string[] => {
    const query = debouncedQuery.trim().toLowerCase();
    if (!query) return addableDomains;
    return addableDomains.filter((domain) =>
      domain.toLowerCase().includes(query),
    );
  }, [addableDomains, debouncedQuery]);

  const visibleDomains = useMemo(
    (): string[] => filteredAddableDomains.slice(0, visibleCount),
    [filteredAddableDomains, visibleCount],
  );

  const isAtMax = safeSelectedDomains.length >= maxDomains;
  const canAdd = !isAtMax && safeAvailableDomains.length > 0;

  useEffect(() => {
    setVisibleCount(DOMAIN_PAGE_SIZE);
  }, [debouncedQuery, isOpen]);

  useEffect(() => {
    if (isAtMax && isOpen) {
      setIsOpen(false);
      setSearchQuery("");
    }
  }, [isAtMax, isOpen]);

  const handleAddDomain = (domain: string): void => {
    if (selectedSet.has(domain) || safeSelectedDomains.length >= maxDomains) {
      return;
    }
    onChange([...safeSelectedDomains, domain]);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleRemoveDomain = (domain: string): void => {
    onChange(safeSelectedDomains.filter((value) => value !== domain));
  };

  const handleOpenChange = (open: boolean): void => {
    if (open && isAtMax) return;
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const isBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (isBottom && visibleCount < filteredAddableDomains.length) {
      setVisibleCount((prev) => prev + DOMAIN_PAGE_SIZE);
    }
  };

  if (isLoading) {
    return (
      <section
        className={cn(
          "rounded-lg border border-border bg-card p-4",
          className,
        )}
        aria-busy
        aria-label="Loading domain selector"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-10" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-8 w-32 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-8 w-24 rounded-full" />
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn("rounded-lg border border-border bg-card p-4", className)}
      aria-label="Select domains to compare"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium tracking-tight text-foreground">
          Select Domains
        </h2>
        <p
          className={cn(
            "tabular-nums text-xs text-muted-foreground",
            isAtMax && "text-primary",
          )}
        >
          {safeSelectedDomains.length} / {maxDomains}
        </p>
      </div>

      <div
        className="flex flex-wrap items-center gap-1.5"
        role="list"
        aria-label="Selected compare domains"
      >
        {safeSelectedDomains.map((domain) => {
          const color = domainDotColor(domain, safeAvailableDomains);
          return (
            <button
              key={domain}
              type="button"
              role="listitem"
              onClick={() => handleRemoveDomain(domain)}
              className="inline-flex max-w-[14rem] shrink-0 items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted cursor-pointer"
              title={`${domain} — click to remove`}
              aria-label={`Remove ${domain} from compare`}
            >
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden
              />
              <span className="truncate">{domain}</span>
              <X className="size-3 shrink-0 text-muted-foreground" />
            </button>
          );
        })}

        {canAdd ? (
          <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                aria-label="Add domain to compare"
              >
                <Plus className="size-3.5" />
                Add domain
                <ChevronDown className="size-3 opacity-60" />
              </button>
            </PopoverTrigger>

            <PopoverContent
              className="w-[min(300px,calc(100vw-2rem))] rounded-xl border-border p-2"
              align="start"
            >
              <div className="sticky top-0 mb-2 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
                <Search className="size-4 shrink-0 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search domain..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>

              <div
                className="hide-scrollbar max-h-[220px] space-y-0.5 overflow-y-auto px-1"
                onScroll={handleScroll}
              >
                {filteredAddableDomains.length === 0 ? (
                  <div className="py-5 text-center text-sm text-muted-foreground">
                    No more domains.
                  </div>
                ) : (
                  visibleDomains.map((domain) => (
                    <button
                      key={domain}
                      type="button"
                      className="group flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
                      onClick={() => handleAddDomain(domain)}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className="size-2 shrink-0 rounded-full"
                          style={{
                            backgroundColor: domainDotColor(
                              domain,
                              safeAvailableDomains,
                            ),
                          }}
                          aria-hidden
                        />
                        <span className="truncate text-sm text-foreground/90">
                          {domain}
                        </span>
                      </span>
                      <Plus className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                    </button>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
        ) : null}

        {safeSelectedDomains.length === 0 ? (
          <p className="w-full pt-1 text-sm text-muted-foreground">
            Pick up to {maxDomains} domains to compare side-by-side.
          </p>
        ) : null}

        {isAtMax ? (
          <p className="w-full pt-1 text-xs text-muted-foreground">
            Maximum of {maxDomains} domains reached. Remove one to add another.
          </p>
        ) : null}
      </div>
    </section>
  );
}

export const CompareDomainSelector = memo(CompareDomainSelectorComponent);
CompareDomainSelector.displayName = "CompareDomainSelector";
